import { useState, useEffect } from "react";
import { fmtLogDay } from "@/lib/tz";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Shield, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CRIMSON = "#bf3b4f";
const GREEN = "#00c86e";

export function TrustedDevicesDialog({ open, onOpenChange }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState(null);

  useEffect(() => {
    if (open) load();
  }, [open]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.trustedDevices();
      setDevices(r.devices || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load trusted devices.");
    } finally {
      setLoading(false);
    }
  };

  const revoke = async (id) => {
    setRevoking(id);
    try {
      await api.revokeTrustedDevice(id);
      toast.success("Device forgotten. You'll need to verify with 2FA next time.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't revoke this device.");
    } finally {
      setRevoking(null);
    }
  };

  const revokeAll = async () => {
    if (!confirm("Forget all devices? You'll need to verify with 2FA on every device next time.")) return;
    setLoading(true);
    try {
      const r = await api.revokeAllTrustedDevices();
      toast.success(r.message || "All devices forgotten.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't revoke devices.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return fmtLogDay(iso);
    } catch {
      return "—";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="trusted-devices-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" style={{ color: GREEN }} /> Trusted devices
          </DialogTitle>
          <DialogDescription>
            Devices you've chosen to trust for 30 days — you won't be asked for a 2FA code when signing in from these.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : devices.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-500">
            No trusted devices yet. Next time you sign in with 2FA, check "Trust this device" to skip verification for 30 days.
          </div>
        ) : (
          <>
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="trusted-devices-list">
              {devices.map((d) => (
                <div key={d.id} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 hover:border-slate-300 transition-colors">
                  <Shield className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{d.label || "Unknown device"}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Trusted {formatDate(d.created_at)}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => revoke(d.id)}
                    disabled={revoking === d.id}
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 shrink-0"
                    data-testid={`revoke-device-${d.id}`}
                  >
                    {revoking === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              ))}
            </div>

            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700 leading-relaxed">
                If you no longer have access to a device, remove it immediately. Anyone with physical access to a trusted device can sign in without 2FA.
              </p>
            </div>
          </>
        )}

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="td-close" className="h-10">
            Close
          </Button>
          {devices.length > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={revokeAll}
              disabled={loading}
              className="h-10 text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
              data-testid="td-revoke-all"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Forget all devices
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
